import { workingStatusLight } from './constants'

export interface WorkingItemCompletionState {
  done?: boolean
  isPriority?: boolean
}

export const NO_WORKING_PROJECT_LABEL = '不分專案'

export function workingProjectLabel(projectName: string | null | undefined) {
  return projectName?.trim() || NO_WORKING_PROJECT_LABEL
}

export interface WorkingProjectLike {
  id: string
  name: string
  groupName?: string
  type?: string
}

export function workingProjectRefs(item: { projects?: WorkingProjectLike[]; projectName?: string | null }) {
  if (Array.isArray(item.projects) && item.projects.length) return item.projects
  const name = item.projectName?.trim()
  return name ? [{ id: name, name, groupName: '' }] : []
}

export function workingProjectSummary(projects: WorkingProjectLike[]) {
  if (!projects.length) return NO_WORKING_PROJECT_LABEL
  if (projects.length === 1) return projects[0].name
  const groupNames = [...new Set(projects.map((project) => project.groupName?.trim()).filter(Boolean))]
  return groupNames.length === 1 ? `(${groupNames[0]}) ×${projects.length}` : `${projects[0].name} +${projects.length - 1}`
}

export function workingProjectNames(item: { projects?: WorkingProjectLike[]; projectName?: string | null }) {
  return workingProjectRefs(item).map((project) => project.name)
}

export type WorkingItemSortKey = 'project' | 'startDate' | 'dueDate' | 'status'
export type WorkingItemSortDirection = 'asc' | 'desc'
export interface WorkingItemSort {
  key: WorkingItemSortKey
  direction: WorkingItemSortDirection
}

export function nextWorkingItemSort(current: WorkingItemSort | null, key: WorkingItemSortKey): WorkingItemSort | null {
  if (!current || current.key !== key) return { key, direction: 'asc' }
  if (current.direction === 'asc') return { key, direction: 'desc' }
  return null
}

interface WorkingSortableItem {
  projects?: WorkingProjectLike[]
  projectName?: string | null
  startDate: string
  dueDate: string
  status: string
}

const workingStatusPriority = { red: 0, yellow: 1, green: 2, white: 3 } as const

function workingDateValue(value: string) {
  const normalized = value.trim()
  if (!normalized || /^(?:tbd|—|-)$/i.test(normalized)) return null
  const iso = normalized.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/)
  const monthDay = normalized.match(/^(\d{1,2})[-/](\d{1,2})(?:[-/](\d{2,4}))?$/)
  const chinese = normalized.match(/^(\d{1,2})\s*月\s*(\d{1,2})\s*日?$/)
  const year = iso ? Number(iso[1]) : monthDay?.[3] ? Number(monthDay[3]) + (monthDay[3].length === 2 ? 2000 : 0) : 2000
  const month = Number(iso?.[2] ?? monthDay?.[1] ?? chinese?.[1])
  const day = Number(iso?.[3] ?? monthDay?.[2] ?? chinese?.[2])
  if (!month || !day) return null
  const timestamp = Date.UTC(year, month - 1, day)
  const parsed = new Date(timestamp)
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day ? timestamp : null
}

export function sortWorkingItems<T extends WorkingSortableItem>(rows: T[], sort: WorkingItemSort | null): T[] {
  if (!sort) return rows
  return rows.map((row, index) => ({ row, index })).sort((left, right) => {
    let compared = 0
    if (sort.key === 'project') {
      const leftProject = workingProjectNames(left.row).join('\u0000').trim()
      const rightProject = workingProjectNames(right.row).join('\u0000').trim()
      if (!leftProject || !rightProject) {
        if (!leftProject && !rightProject) return left.index - right.index
        return leftProject ? -1 : 1
      }
      compared = leftProject.localeCompare(rightProject, 'en', { numeric: true, sensitivity: 'base' })
    } else if (sort.key === 'startDate' || sort.key === 'dueDate') {
      const leftDate = workingDateValue(left.row[sort.key])
      const rightDate = workingDateValue(right.row[sort.key])
      if (leftDate === null || rightDate === null) {
        if (leftDate === null && rightDate === null) return left.index - right.index
        return leftDate === null ? 1 : -1
      }
      compared = leftDate - rightDate
    } else if (sort.key === 'status') {
      compared = workingStatusPriority[workingStatusLight(left.row.status)] - workingStatusPriority[workingStatusLight(right.row.status)]
    }
    return compared === 0 ? left.index - right.index : compared * (sort.direction === 'asc' ? 1 : -1)
  }).map(({ row }) => row)
}

export function enforceWorkingItemCompletion<T extends object>(
  values: T,
  current: WorkingItemCompletionState = {},
): T {
  const next = { ...values } as T & WorkingItemCompletionState
  const done = next.done ?? current.done ?? false
  if (done && (next.isPriority === true || current.isPriority === true)) next.isPriority = false
  return next
}
